import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckRequestEntity } from "../check/schema/check-requests.schema";
import { FieldValues, useForm } from "react-hook-form";
import { TransferRequestFilter } from "@/components/common/section-components/filters/TransferRequestFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import dayjs from "dayjs";
import { formatCurrency } from "@/utils/format-currency";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { TransferRequestStatus } from "@/constants/entity-status";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { TransferRequestActions } from "@/components/common/section-components/action/TransferRequestActions";
import { TransferRequestHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  fetchTransferRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";

export const TransferRequestsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { handleSubmit, setValue, getValues, control } = useForm();

  const [loadingTransferRequests, setLoadingTransferRequests] =
    useState<boolean>(false);

  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const [transferRequests, setTransferRequests] = useState<{
    listado: CheckRequestEntity[];
    totalPages: number;
    totalRecords: number;
    pendingRequests: boolean;
  }>({
    listado: [],
    pendingRequests: false,
    totalPages: 1,
    totalRecords: 10,
  });

  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchTransferRequests = async (filters?: any, showLoad = true) => {
    await fetchTransferRequestsHandler({
      filters,
      showLoad,
      setLoadingTransferRequests,
      setTransferRequests,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchTransferRequests();
    }
  }, [isActive]);

  usePolling({
    callback: () => {
      if (!loadingTransferRequests)
        fetchTransferRequests({ ...currentFilters, "P.Pagina": page }, false);
    },
    delay: 10000,
    isActive: isActive && !loadingTransferRequests,
  });

  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters,
    });
  };

  const { fetchPermissions } = useRoles();
  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchTransferRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      fetchPermissions,
      navigate,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchTransferRequests({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.TRANSFER_REQUEST_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };
  const getActionElements = (item: any) => (
    <TransferRequestActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Transferencias" loading={loadingTransferRequests} />

      <TransferRequestFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        submitFilters={submitFilters}
        control={control}
        disableFilters={loadingTransferRequests}
        maxDate={dayjs()}
      />

      <Flex flexDir="column">
        {Boolean(
          !loadingTransferRequests && !transferRequests?.listado?.length,
        ) && <NoData />}
        {Boolean(
          !loadingTransferRequests && transferRequests?.listado?.length,
        ) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                transferRequests?.listado?.map((el: CheckRequestEntity) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: `$${formatCurrency(el?.monto)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as TransferRequestStatus}

                    />
                  ),
                })) ?? []
              }
              headers={TransferRequestHeaderElements}
              actionElements={getActionElements}
            />
            {transferRequests?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={transferRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
